import React from 'react';
import { useTranslation } from 'react-i18next';
import { CircularProgress } from '@mui/material';
import './AdminModal.css';

const BASE_LANG = 'en';

export default function EditMenuItemModal({
  name,
  description,
  price,
  section,
  category,
  imagePreview,
  sectionOptions,
  onNameChange,
  onDescriptionChange,
  onPriceChange,
  onSectionChange,
  onCategoryChange,
  onImageChange,
  onRemoveImage,
  onSubmit,
  onCancel,
  // translations
  supportedLanguages,
  activeLangTab,
  onActiveLangTabChange,
  translations,
  onTranslationChange,
  translating,
  onAutoTranslate,
}) {
  const { t } = useTranslation();

  return (
    <div className="adm-modal-overlay" onClick={onCancel}>
      <div className="adm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="adm-modal__header">
          <h3 className="adm-modal__title">{t('admin.editMenuItem.title')}</h3>
          <button
            className="btn btn-secondary adm-modal__close"
            onClick={onCancel}
          >
            ✕
          </button>
        </div>

        <form onSubmit={onSubmit}>
          <div className="language-tabs-header">
            {supportedLanguages.map((lang) => {
              const isBase = lang.code === BASE_LANG;
              const trans = isBase
                ? { name, description, isAutoTranslated: false }
                : translations[lang.code];
              const isEmpty = !trans.name;
              const isAuto = trans.isAutoTranslated;
              return (
                <button
                  key={lang.code}
                  type="button"
                  className={`language-tab-btn ${activeLangTab === lang.code ? 'active' : ''}`}
                  onClick={() => onActiveLangTabChange(lang.code)}
                >
                  <span>{lang.flag}</span>
                  <span>{lang.nativeName}</span>
                  {isBase && <span className="lang-status" title={t('translation.baseLanguage')}>📝</span>}
                  {!isBase && isAuto && !isEmpty && <span className="lang-status" title={t('translation.autoTranslated')}>🤖</span>}
                  {!isBase && !isAuto && !isEmpty && <span className="lang-status" title={t('translation.manuallyEdited')}>✓</span>}
                  {isEmpty && !isBase && <span className="lang-status" style={{ opacity: 0.5 }}>—</span>}
                </button>
              );
            })}
          </div>

          {supportedLanguages.map((lang) => {
            const isBase = lang.code === BASE_LANG;
            const trans = isBase
              ? { name, description, isAutoTranslated: false }
              : translations[lang.code];
            return (
              <div
                key={lang.code}
                style={{
                  display: activeLangTab === lang.code ? 'block' : 'none',
                  direction: lang.rtl ? 'rtl' : 'ltr',
                }}
              >
                <div className="form-group">
                  <label>{t('admin.addMenuItem.itemName')} ({lang.nativeName}){isBase ? ' *' : ''}</label>
                  <input
                    type="text"
                    className="form-input"
                    value={trans.name}
                    onChange={(e) => {
                      if (isBase) {
                        onNameChange(e.target.value);
                      } else {
                        onTranslationChange(lang.code, 'name', e.target.value);
                      }
                    }}
                    placeholder={t('admin.addMenuItem.itemNamePlaceholder')}
                    style={{ textAlign: lang.rtl ? 'right' : 'left' }}
                    required={isBase}
                  />
                </div>

                <div className="form-group">
                  <label>{t('admin.addMenuItem.description')} ({lang.nativeName})</label>
                  <textarea
                    className="form-input"
                    rows={3}
                    value={trans.description}
                    onChange={(e) => {
                      if (isBase) {
                        onDescriptionChange(e.target.value);
                      } else {
                        onTranslationChange(lang.code, 'description', e.target.value);
                      }
                    }}
                    placeholder={t('admin.addMenuItem.descriptionPlaceholder')}
                    style={{ textAlign: lang.rtl ? 'right' : 'left', resize: 'vertical' }}
                  />
                </div>

                {!isBase && trans.isAutoTranslated && (
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: '0.5rem',
                    padding: '0.5rem', background: '#f0f9ff', borderRadius: '4px',
                    fontSize: '0.875rem', color: '#0369a1', marginBottom: '1rem',
                  }}>
                    <span>🤖</span>
                    <span>{t('translation.autoTranslatedNote')}</span>
                  </div>
                )}
              </div>
            );
          })}

          <button
            type="button"
            className="btn btn-primary"
            onClick={onAutoTranslate}
            disabled={translating || !name}
            style={{ marginBottom: '1rem' }}
          >
            {translating ? (
              <>
                <CircularProgress size={18} color="inherit" />
                <span style={{ marginInlineStart: '0.5rem' }}>{t('translation.translating')}</span>
              </>
            ) : (
              <>
                <span>🔄</span>
                <span style={{ marginInlineStart: '0.5rem' }}>{t('translation.autoTranslateOthers')}</span>
              </>
            )}
          </button>

          <div className="form-group">
            <label>{t('admin.addMenuItem.price')} *</label>
            <input
              type="number"
              className="form-input"
              value={price}
              onChange={(e) => onPriceChange(e.target.value)}
              placeholder={t('admin.addMenuItem.pricePlaceholder')}
              step="0.01"
              required
            />
          </div>

          <div className="form-group">
            <label>{t('admin.addMenuItem.sectionLabel')}</label>
            <select
              className="form-input"
              value={section}
              onChange={(e) => onSectionChange(e.target.value)}
              required
            >
              <option value="">{t('admin.addMenuItem.sectionPlaceholder')}</option>
              {sectionOptions.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>{t('admin.addMenuItem.categoryLabel')}</label>
            <input
              type="text"
              className="form-input"
              value={category}
              onChange={(e) => onCategoryChange(e.target.value)}
              placeholder={t('admin.addMenuItem.categoryPlaceholder')}
            />
          </div>

          <div className="form-group">
            <label>{t('admin.addMenuItem.uploadImage')}</label>
            <div
              className={`file-upload-area ${imagePreview ? 'has-file' : ''}`}
              onClick={() => document.getElementById('edit-item-image').click()}
            >
              {imagePreview ? (
                <div className="image-preview">
                  <img src={imagePreview} alt="Preview" />
                  <div>{t('admin.addMenuItem.clickToChange')}</div>
                </div>
              ) : (
                <div className="upload-text">
                  <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📸</div>
                  <div>{t('admin.addMenuItem.clickToUpload')}</div>
                  <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)', marginTop: '0.5rem' }}>
                    {t('admin.addMenuItem.imageSpecs')}
                  </div>
                </div>
              )}
            </div>
            <input
              type="file"
              id="edit-item-image"
              accept="image/*"
              onChange={onImageChange}
              style={{ display: 'none' }}
            />
            {imagePreview && (
              <button
                type="button"
                className="btn btn-secondary btn-small"
                onClick={onRemoveImage}
                style={{ marginTop: '0.5rem' }}
              >
                {t('admin.editMenuItem.removeImage')}
              </button>
            )}
          </div>

          <div className="adm-modal__actions">
            <button type="submit" className="btn btn-primary">
              {t('admin.editMenuItem.updateButton')}
            </button>
            <button type="button" className="btn btn-secondary" onClick={onCancel}>
              {t('common.cancel')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
